import React, { useState, useEffect } from 'react';
import { FiPackage, FiTruck, FiCheckCircle, FiClock, FiSearch, FiX } from 'react-icons/fi';
import { deliveryService } from '../../services/api';
import { toast } from 'react-toastify';

const AdminDelivery = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatientForDetails, setSelectedPatientForDetails] = useState(null);

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const data = await deliveryService.getAll();
      setDeliveries(data);
    } catch (e) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      let deliveryDate = null;
      if (newStatus === 'delivered') {
        deliveryDate = new Date().toISOString().split('T')[0];
      }
      await deliveryService.updateStatus({ id, delivery_status: newStatus, delivery_date: deliveryDate });
      toast.success('Status updated!');
      fetchDeliveries();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = deliveries.filter(d => 
    !searchQuery || 
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (d.invoice_no && d.invoice_no.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getStatusBadge = (status) => {
    switch(status) {
      case 'delivered': return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 w-max"><FiCheckCircle /> Delivered</span>;
      case 'ready': return <span className="px-3 py-1 bg-blue-100 text-syana-blue-700 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 w-max"><FiPackage /> Ready</span>;
      default: return <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 w-max"><FiClock /> Processing</span>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      {/* Header */}
      <div className="bg-white rounded-xl p-8 shadow-xl shadow-slate-200/40 border border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <FiTruck size={120} />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Order Deliveries</h2>
            <p className="text-slate-500 font-medium mt-1">Track manufacturing, readiness, and handover of patient glasses.</p>
          </div>
          
          <div className="relative w-full md:w-auto">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search by Name or Invoice..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full md:w-80 pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md shadow-slate-200/50 border border-orange-100 flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center text-xl"><FiClock /></div>
          <div>
            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Processing</h4>
            <span className="text-2xl font-black text-slate-800">{deliveries.filter(d => (!d.delivery_status || d.delivery_status === 'processing')).length}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md shadow-slate-200/50 border border-blue-100 flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-syana-blue-600 flex items-center justify-center text-xl"><FiPackage /></div>
          <div>
            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Ready for Pickup</h4>
            <span className="text-2xl font-black text-slate-800">{deliveries.filter(d => d.delivery_status === 'ready').length}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md shadow-slate-200/50 border border-green-100 flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center text-xl"><FiCheckCircle /></div>
          <div>
            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Delivered</h4>
            <span className="text-2xl font-black text-slate-800">{deliveries.filter(d => d.delivery_status === 'delivered').length}</span>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Customer</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Order Details</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Payment</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Delivery Status</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan="5" className="py-14 text-center font-bold text-slate-400">Loading Orders...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="5" className="py-14 text-center font-bold text-slate-400">No Dispensing Orders Found.</td></tr>
              ) : filtered.map(d => {
                let disp = d.dispensing_details;
                if (typeof disp === 'string') {
                  try { disp = JSON.parse(disp); } catch(e) { disp = null; }
                }
                const balance = d.payment_balance !== null ? parseFloat(d.payment_balance) : null;

                return (
                  <tr key={d.id} className="hover:bg-slate-50/60 transition-colors">
                    {/* Customer */}
                    <td className="px-6 py-4">
                      <div 
                        className="font-black text-syana-blue-600 hover:text-syana-blue-800 uppercase cursor-pointer underline decoration-syana-blue-200 underline-offset-4"
                        onClick={() => setSelectedPatientForDetails(d)}
                        title="View Full Registration Details"
                      >
                        {d.name}
                      </div>
                      <div className="text-xs font-semibold text-slate-400 mt-0.5">{d.contact || 'No Contact'}</div>
                    </td>

                    {/* Order Details */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        {/* Lens Info */}
                        {disp?.lens_type && (
                          <div className="flex flex-col">
                             <div className="flex items-center gap-2 text-xs">
                               <span className="w-12 text-[9px] uppercase tracking-widest text-slate-400 font-bold">Lens:</span>
                               <span className="font-bold text-slate-800 capitalize">{disp.lens_type.replace(/_/g, ' ')}</span>
                             </div>
                             {(disp.cr39_bifocal_type || disp.cr39_bifocal_shape) && disp.lens_type.toLowerCase() === 'cr39 bifocal' && (
                               <div className="pl-14 text-[10px] text-slate-500 font-bold flex gap-2">
                                  <span>{disp.cr39_bifocal_type} type</span>
                                  <span>•</span>
                                  <span>{disp.cr39_bifocal_shape.replace(/_/g, ' ')} shape</span>
                               </div>
                             )}
                          </div>
                        )}
                        
                        {/* Frame Info */}
                        {disp?.frame_brand && (
                          <div className="flex flex-col pt-1 border-t border-slate-50">
                             <div className="flex items-center gap-2 text-xs">
                               <span className="w-12 text-[9px] uppercase tracking-widest text-slate-400 font-bold">Frame:</span>
                               <span className="font-bold text-slate-800">{disp.frame_brand} ({disp.frame_material})</span>
                             </div>
                             <div className="pl-14 text-[10px] text-slate-500 font-bold flex gap-2">
                                <span className="capitalize">{disp.frame_demographic}</span>
                                {disp.frame_material === 'metal' && disp.frame_metal_type && (
                                  <>
                                    <span>•</span>
                                    <span className="capitalize">{disp.frame_metal_type.replace(/_/g, ' ')}</span>
                                  </>
                                )}
                             </div>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Payment Info */}
                    <td className="px-6 py-4">
                      {d.invoice_no ? (
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="font-black text-slate-700">{d.invoice_no}</span>
                          {balance > 0 ? (
                             <span className="font-bold text-orange-600">Dues: Rs. {balance.toFixed(2)}</span>
                          ) : balance === 0 ? (
                             <span className="font-bold text-green-600">Fully Paid</span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs font-bold text-slate-400 italic">No Invoice Linked</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 relative group w-max">
                        {getStatusBadge(d.delivery_status)}
                        {d.delivery_date && (
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                            {d.delivery_date}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Action */}
                    <td className="px-6 py-4">
                      <select 
                        className="bg-white border text-xs font-bold border-slate-200 text-slate-700 rounded-lg px-3 py-2 outline-none focus:border-syana-blue-400 transition-colors cursor-pointer"
                        value={d.delivery_status || 'processing'}
                        onChange={(e) => handleStatusChange(d.id, e.target.value)}
                      >
                        <option value="processing">Mark Processing</option>
                        <option value="ready">Mark Ready</option>
                        <option value="delivered">Mark Delivered</option>
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Patient Registration Details Modal */}
      {selectedPatientForDetails && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 md:p-6" onClick={() => setSelectedPatientForDetails(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h3 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-widest">{selectedPatientForDetails.name}</h3>
                <p className="text-[10px] md:text-xs font-bold text-syana-blue-600 mt-1 uppercase tracking-widest">Full Registration & Clinical Details</p>
              </div>
              <button 
                onClick={() => setSelectedPatientForDetails(null)} 
                className="p-3 bg-white border border-slate-200 hover:bg-slate-100 rounded-full transition-colors text-slate-500 shadow-sm"
              >
                <FiX size={20} />
              </button>
            </div>
            
            <div className="p-6 flex-1 overflow-y-auto bg-white space-y-8 no-scrollbar">
              
              {/* Basic Info */}
              <div>
                <h4 className="text-xs md:text-sm font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b border-slate-100 pb-2">Personal Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Age / DOB</span>
                    <span className="block text-sm font-black text-slate-800">{selectedPatientForDetails.age || '-'} Yrs ({selectedPatientForDetails.dob || '-'})</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Contact</span>
                    <span className="block text-sm font-black text-slate-800">{selectedPatientForDetails.contact || '-'}</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 col-span-2">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Address</span>
                    <span className="block text-sm font-black text-slate-800">{selectedPatientForDetails.address || '-'}</span>
                  </div>
                </div>
              </div>

              {/* History */}
              <div>
                <h4 className="text-xs md:text-sm font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b border-slate-100 pb-2">Medical History</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                   <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100">
                    <span className="block text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-1">Medical</span>
                    <span className="block text-sm font-black text-slate-800 whitespace-pre-wrap">{selectedPatientForDetails.medical_history || 'None'}</span>
                  </div>
                  <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100">
                    <span className="block text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-1">Family</span>
                    <span className="block text-sm font-black text-slate-800 whitespace-pre-wrap">{selectedPatientForDetails.family_history || 'None'}</span>
                  </div>
                  <div className="bg-red-50/50 p-4 rounded-2xl border border-red-100">
                    <span className="block text-[10px] uppercase tracking-widest text-red-500 font-bold mb-1">Allergies</span>
                    <span className="block text-sm font-black text-slate-800 whitespace-pre-wrap">{selectedPatientForDetails.allergies || 'None'}</span>
                  </div>
                </div>
              </div>

              {/* General Optical fields */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                 {[
                   { label: 'Ocular Movement', val: selectedPatientForDetails.ocular_movement },
                   { label: 'Convergence', val: selectedPatientForDetails.convergence },
                   { label: 'Color Vision', val: selectedPatientForDetails.color_vision },
                   { label: 'First Wearer?', val: selectedPatientForDetails.first_wear },
                   { label: 'Ophthalmoscopy', val: selectedPatientForDetails.ophthalmoscopy },
                   { label: 'Slit Lamp', val: selectedPatientForDetails.slit_lamp }
                 ].map((item, idx) => item.val ? (
                   <div key={idx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <span className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">{item.label}</span>
                      <span className="block text-sm font-black text-slate-800">{item.val}</span>
                   </div>
                 ) : null)}
              </div>

              {/* Clinical JSON Blocks */}
              <div>
                <h4 className="text-xs md:text-sm font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b border-slate-100 pb-2">Clinical Readings</h4>
                <div className="space-y-6">
                  {Object.entries({
                    'Visual Acuity': selectedPatientForDetails.visual_acuity,
                    'Habitual RX': selectedPatientForDetails.habitual_rx,
                    'Auto Refraction': selectedPatientForDetails.auto_refraction,
                    'Retinoscopy': selectedPatientForDetails.retinoscopy,
                    'Subjective Refraction': selectedPatientForDetails.subjective,
                    'Dispensing Specifications': selectedPatientForDetails.dispensing_details
                  }).map(([title, jsonStr]) => {
                    if (!jsonStr || jsonStr === 'null' || jsonStr === '') return null;
                    let parsed;
                    try { parsed = JSON.parse(jsonStr); } catch(e) { return null; }
                    if (Object.keys(parsed).length === 0) return null;
                    
                    // Renderer for Standard Optical Readings (SPH, CYL, etc)
                    if (['Habitual RX', 'Auto Refraction', 'Retinoscopy', 'Subjective Refraction'].includes(title)) {
                      return (
                        <div key={title} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                           <div className="bg-slate-100/80 px-4 py-3 border-b border-slate-200">
                             <h5 className="text-[10px] font-black uppercase tracking-widest text-syana-blue-600">{title}</h5>
                           </div>
                           <div className="overflow-x-auto">
                             <table className="w-full text-center divide-y divide-slate-200">
                               <thead className="bg-slate-50/50">
                                 <tr>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200 w-16">Eye</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">SPH</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">CYL</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">AXIS</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">DVA</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">PH</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">ADD</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">NVA</th>
                                 </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-200 bg-white">
                                 {['R', 'L'].map((eye) => {
                                   if(!parsed[eye]) return null;
                                   return (
                                     <tr key={`${title}-${eye}`} className="hover:bg-slate-50/50 transition-colors">
                                       <td className="px-4 py-3 border-r border-slate-200 bg-slate-50/50">
                                         <span className="font-black text-sm text-slate-900">{eye}</span>
                                       </td>
                                       {['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map((field) => (
                                         <td key={`${title}-${eye}-${field}`} className="p-3 border-r border-slate-200 last:border-r-0">
                                            <span className="font-bold text-sm text-slate-800">{parsed[eye][field] || '-'}</span>
                                         </td>
                                       ))}
                                     </tr>
                                   )
                                 })}
                               </tbody>
                             </table>
                           </div>
                        </div>
                      );
                    }

                    // Renderer for Visual Acuity
                    if (title === 'Visual Acuity') {
                      return (
                        <div key={title} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                           <div className="bg-slate-100/80 px-4 py-3 border-b border-slate-200">
                             <h5 className="text-[10px] font-black uppercase tracking-widest text-purple-600">{title}</h5>
                           </div>
                           <div className="overflow-x-auto">
                             <table className="w-full text-center divide-y divide-slate-200">
                               <thead className="bg-slate-50/50">
                                 <tr>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200 w-16">Eye</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">VA (Unaided)</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">VA (PH)</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">VA (A)</th>
                                 </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-200 bg-white">
                                 {['R', 'L'].map((eye) => {
                                   if (!parsed[eye]) return null;
                                   return (
                                     <tr key={`va-${eye}`} className="hover:bg-slate-50/50 transition-colors">
                                       <td className="px-4 py-3 border-r border-slate-200 bg-slate-50/50">
                                         <span className="font-black text-sm text-slate-900">{eye}</span>
                                       </td>
                                       {['unaided', 'ph', 'aided'].map((type) => {
                                         const val = parsed[eye][type];
                                         if(!val) return <td key={type} className="p-3 border-r border-slate-200">-</td>;
                                         const displayStr = val.num && val.den ? `${val.num}/${val.den} ${val.sign !== 'none' ? val.sign : ''}` : '-';
                                         return (
                                           <td key={`va-${eye}-${type}`} className="p-3 border-r border-slate-200 last:border-r-0">
                                             <span className="font-bold text-sm text-slate-800">{displayStr}</span>
                                           </td>
                                         )
                                       })}
                                     </tr>
                                   )
                                 })}
                               </tbody>
                             </table>
                           </div>
                        </div>
                      )
                    }

                    // Renderer for Dispensing Specifications (Matches NewPatient Review Form)
                    if (title === 'Dispensing Specifications') {
                      return (
                         <div key={title} className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4 shadow-sm">
                           <div className="border-b border-slate-200 pb-2">
                             <h5 className="font-bold text-teal-600 uppercase tracking-widest text-[10px]">Dispensing Specifications</h5>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                             <div>
                                <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mb-1">Measurements</p>
                                <p className="font-black text-slate-900 text-sm">PD: {parsed.pd || '-'} mm</p>
                                <p className="font-black text-slate-900 text-sm mt-1">Seg Height: {parsed.segment_height || '-'} mm</p>
                             </div>
                             <div>
                                <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mb-1">Frame Settings</p>
                                <p className="font-black text-slate-900 text-sm">{parsed.frame_brand || 'No Brand Selected'}</p>
                                <div className="mt-1 flex flex-col gap-0.5">
                                  <p className="font-black text-slate-900 text-xs capitalize">{parsed.frame_demographic || '-'} | {parsed.frame_material || '-'}</p>
                                  {parsed.frame_material === 'metal' && parsed.frame_metal_type && (
                                    <p className="text-[10px] font-bold text-syana-blue-600 capitalize">Style: {parsed.frame_metal_type.replace(/_/g, ' ')}</p>
                                  )}
                                </div>
                             </div>
                             <div>
                                <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mb-1">Lens Request</p>
                                <p className="font-black text-slate-900 text-sm capitalize">{parsed.lens_type ? parsed.lens_type.replace(/_/g, ' ') : '-'}</p>
                                {(parsed.cr39_bifocal_type || parsed.cr39_bifocal_shape) && (
                                  <div className="mt-1 text-[10px] font-bold text-teal-600 flex flex-col gap-0.5">
                                    <p>Type: {parsed.cr39_bifocal_type}</p>
                                    <p>Shape: {parsed.cr39_bifocal_shape?.replace(/_/g, ' ')}</p>
                                  </div>
                                )}
                                <p className="font-black text-slate-900 text-[10px] mt-2 capitalize bg-slate-100 px-2 py-0.5 rounded w-max">Reading: {parsed.reading_glasses || '-'}</p>
                             </div>
                           </div>
                           {parsed.remarks && (
                             <div className="pt-4 border-t border-slate-100 mt-4">
                               <p className="text-slate-400 font-bold text-[9px] uppercase tracking-widest mb-1">Remarks</p>
                               <p className="text-xs font-bold text-slate-700 italic border-l-2 border-teal-200 pl-3">{parsed.remarks}</p>
                             </div>
                           )}
                         </div>
                      );
                    }

                    // Fallback for Generic Objects
                    const renderNestedValues = (obj, parentKey = '') => {
                      return Object.entries(obj).map(([k, v]) => {
                         if (v && typeof v === 'object') {
                           return (
                             <div key={k} className="col-span-full mt-2">
                               <div className="text-[10px] font-black uppercase tracking-widest text-slate-800 bg-slate-100 px-2 py-1 rounded w-max mb-2 border border-slate-200">Eye: {k}</div>
                               <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pl-2 border-l-2 border-slate-200">
                                  {renderNestedValues(v, k)}
                               </div>
                             </div>
                           );
                         }
                         return v ? (
                           <div key={`${parentKey}-${k}`} className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                             <span className="block text-[8px] md:text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">{k.replace(/_/g, ' ')}</span>
                             <span className="block text-xs md:text-sm font-black text-slate-800 capitalize">{String(v).replace(/_/g, ' ')}</span>
                           </div>
                         ) : null;
                      });
                    };

                    return (
                      <div key={title} className="bg-blue-50/30 p-4 rounded-2xl border border-blue-100">
                        <h5 className="text-[10px] font-black uppercase tracking-widest text-syana-blue-600 mb-3">{title}</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {renderNestedValues(parsed)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
            
            {/* Footer */}
            <div className="p-4 md:p-6 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button 
                onClick={() => setSelectedPatientForDetails(null)}
                className="w-full md:w-auto px-8 py-3.5 bg-slate-900 text-white font-bold rounded-xl text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-slate-200 hover:-translate-y-0.5 hover:bg-slate-800 transition-all active:scale-95"
              >
                 Close Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDelivery;
